<template>
  <div
    :class="{
      'fact-finder-filters-fi': true,
      'fact-finder-filters-fi--closed': hideFilters,
    }"
  >
    <div
      :class="{
        'fact-finder-filters-fi__label': true,
        'fact-finder-filters-fi__label--fixed': localFixed,
        'fact-finder-filters-fi__label--leaving': leaving,
        'fact-finder-filters-fi__label--active': activeFilters.length > 0,
      }"
    >
      <div
        class="fact-finder-filters-fi__label__btn"
        @click="onClickLabel"
      >
        <div class="fact-finder-filters-fi__label__text">
          {{ $dict.get('product-List.Facets.Filters.Label') }}

          <span
            v-if="activeFilters.length > 0"
            class="fact-finder-filters-fi__label__count"
            >( {{ activeFilters.length }} )</span
          >
        </div>

        <icon-fi
          class="fact-finder-filters-fi__filter"
          icon="filter"
        />

        <icon-fi
          v-if="activeFilters.length > 0"
          class="fact-finder-filters-fi__clear"
          icon="load-loop"
          @click="$emit('reset-filter')"
        />
      </div>
    </div>
    <div
      :class="{
        'fact-finder-filters-fi__expander': true,
        'fact-finder-filters-fi__expander--closed': hideFilters,
      }"
    >
      <div class="fact-finder-filters-fi__inner">
        <div class="fact-finder-filters-fi__mobile-header">
          <div
            class="fact-finder-filters-fi__mobile-header__text"
            @click="hideFilters = true"
          >
            <icon-fi
              icon="chevron-left"
              class="fi-flex-shrink-0"
            />
            <span class="fi-line-clamp-1 fi-text-ellipsis">
              {{ $dict.get('product-List.Facets.Filters.Label') }}
            </span>
          </div>
          <div
            v-if="activeFilters.length > 0"
            class="fact-finder-filters-fi__mobile-header__clear"
            @click="clearFacets"
          >
            <span class="fi-text-nowrap">
              {{ $dict.get('product-List.Facets.Filters.Clear') }}
            </span>
            <icon-fi
              icon="load-loop"
              @click="$emit('reset-filter')"
            />
          </div>
        </div>

        <div class="fact-finder-filters-fi__list">
          <fact-finder-filter-fi
            v-for="(filter, index) in filters"
            :key="filter.associatedFieldName"
            :filter="filter"
            :hide-filters="hideFilters"
            :active-filters="activeFilters.length > 0"
            :idx="index"
            :loading="loading"
            @filter-changed="(value) => $emit('filter-changed', value)"
          />
        </div>

        <div class="fact-finder-filters-fi__mobile-footer">
          <div
            v-if="activeFilters.length > 0"
            class="fact-finder-filters-fi__mobile-footer__button"
            @click="hideFilters = true"
          >
            {{ $dict.get('product-List.Facets.Filters.Show-Results') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { IconFi } from 'atoms';
import { computed, ref, watch } from 'vue';

import type { ProcessedFactFinderNgFacet } from '@/components/facets/processedFacetTypes';
import { bodyDisableScrollBody, bodyEnableScrollBody } from '@/composables/bodyScroll';
import { useBreakpoints } from '@/composables/breakpoint';

import type { SearchFilter } from '../SearchFactFinderResultsFi.vue';
import FactFinderFilterFi from './fact-finder-filter-fi/FactFinderFilterFi.vue';

defineOptions({
  name: 'FactFinderFiltersFi',
});

const { isMD } = useBreakpoints();

const props = defineProps<{
  filters: ProcessedFactFinderNgFacet[];
  loading: boolean;
  fixed: boolean;
}>();

const emits = defineEmits<{
  'filter-changed': [filter: SearchFilter];
  'reset-filter': [value: void];
}>();

const activeFilters = computed(() => {
  return props.filters.filter((filter) => filter.elements?.find((el) => el?.selectedBool === true));
});

const hideFilters = ref(true);

const FIXED_LEAVE_ANIMATION_DURATION = 300;

const localFixed = ref(props.fixed);
const leaving = ref(false);
let leaveTimeout: ReturnType<typeof setTimeout> | undefined;

watch(
  () => props.fixed,
  (value) => {
    clearTimeout(leaveTimeout);

    if (value) {
      leaving.value = false;
      localFixed.value = true;
      return;
    }

    leaving.value = true;
    leaveTimeout = setTimeout(() => {
      localFixed.value = false;
      leaving.value = false;
    }, FIXED_LEAVE_ANIMATION_DURATION);
  },
);

const clearFacets = () => {
  hideFilters.value = true;
  emits('reset-filter');
};

const onClickLabel = () => {
  if (isMD.value) {
    return;
  }
  hideFilters.value = !hideFilters.value;
};

// Only the breakpoint matters: at md the expander renders inline, so a drawer left open would
// stick around as a full-screen overlay. Deliberately NOT watching windowDimensions - on mobile
// the address bar collapsing while the filter list is scrolled changes the viewport height, which
// closed the drawer mid-use and reset every group (FactFinderFilterFi resets optionsState when
// hideFilters flips to true).
watch(isMD, (value) => {
  if (value) {
    hideFilters.value = true;
  }
});

watch(
  () => hideFilters.value,
  (value) => {
    // Closing always releases the lock, even when md took over in the same tick - otherwise the
    // body stays unscrollable after a resize past the breakpoint.
    if (value) {
      bodyEnableScrollBody();
      return;
    }

    if (isMD.value) {
      return;
    }

    bodyDisableScrollBody();
  },
);
</script>

<style scoped src="./fact-finder-filters-fi" />
